import React, { useContext, useEffect } from 'react'
import { Container } from '@olxbr/ds-web'
import styled from 'styled-components'
import { CheckPinCodeTemplate, getSendMode, SEND_MODES } from '../../templates/CheckPinCodeTemplate'
import { ArrowLeft } from '@olxbr/olx-icons/dist/System'

import PropTypes from 'prop-types'
import { CountdownContext } from '../../../util/providers/CountdownProvider'
import { sendCode2FA } from '../../../api/phoneValidationApiCall'

import { breakpoints } from '../../../util/medias'

import Lurker from '../../../util/lurker'
import { loginWith2FA, loginWith2FAValidation } from '../../../api/loginApiCall'
import { getAttemptCheck2FA } from '../../../api/attemptCheckAPI'
import {
  getModalLoginExtraData,
  isAccountRevalidationExperimentOn,
} from '../../../util/experiments'
import { View } from '../../atoms/View'
import { MANDATORY_TIPIFICATION_KEY } from '../../../util/global.consts'
import storage from '../../../util/storage'

const lurker = new Lurker('phone_2fa')

const issuer = 'AccountsValhallaGreyLogin'

const ContentElement = ({ modalMode, ...props }) => {
  return modalMode ? <View {...props} /> : <Container {...props} />
}

const Wrapper = styled.div`
  flex: 1;
  flex-direction: column;
  margin: auto;
  @media (max-width: ${breakpoints['mobile']}px) {
    margin-left: var(--spacing-2);
    margin-right: var(--spacing-2);
  }
  @media (min-width: 840px) {
    max-width: 600px;
  }
  @media (min-width: ${breakpoints['mobile'] + 1}px) and (max-width: ${breakpoints['tablet']}px) {
    width: 80vw;
  }
`

const ContainerWrapper = styled(ContentElement)`
  @media (max-width: ${breakpoints['mobile']}px) {
    border: none !important;
    padding: 0;
  }
`

const StyledArrow = styled(ArrowLeft)`
  color: var(--color-neutral-130);
`
const ArrowContainer = styled.div`
  cursor: pointer;
  margin-bottom: var(--spacing-4);

  ${({ modalMode }) => modalMode && `margin-bottom: var(--spacing-2);`}
`

const StyledTitle = styled.span.attrs({ className: 'typo-title-large' })`
  font-size: var(--font-size-3);
`

const LOGIN_TYPE = SEND_MODES.PHONE

const CheckPhone2FAPage = ({
  onBackClick,
  onSuccess,
  onAccountNotValidated,
  phone,
  token,
  sessionTokens,
  abTests,
  publicAccountId,
  modalMode,
  onAccountCompleteData,
  defaultSendMode = SEND_MODES.PHONE,
}) => {
  const { lockTime, checkAttemptLockTime } = useContext(CountdownContext)
  const sendMode = getSendMode(true)

  /**
   * Handles errors related to PIN code verification during 2FA phone checks.
   *
   * @param {Object} [error={}] - The error object, typically from an HTTP response.
   * @param {string} [eventName='error'] - The event name for error tracking.
   * @param {*} [_] - Unused parameter.
   * @param {Function} [showError=(error, customMsg = '') => {}] - Callback to display error messages.
   * @param {Function} [onMaxAccountCheckAttemptReached=() => {}] - Callback when max PIN code attempts are reached.
   * @param {Function} [updateRemainingAttempts=() => {}] - Callback to update remaining attempts.
   */
  const onPinCodeError = (
    error = {},
    eventName = 'error',
    _,
    showError = (error, customMsg = '') => {},
    onMaxAccountCheckAttemptReached = () => {},
    updateRemainingAttempts = () => {}
  ) => {
    const {
      response: {
        status,
        data: {
          message,
          details = { ttl: 0, attempts: 2, max_attempts: 4, remaining_attempts: 2 },
        } = {},
      } = {},
    } = error
    const flow = 'check_pincode'
    updateRemainingAttempts(details.remaining_attempts)

    let genericError = 'showGenericError'
    if (eventName !== 'error') {
      genericError = 'showResendCodeError'
    }

    if (status === 401) {
      if (message === 'ACCOUNT_NOT_VALIDATED') {
        onAccountNotValidated(error?.response?.data?.token)

        lurker.viewMessageEvt(eventName, {
          accounts_reason: message.toLowerCase(),
          accounts_status_code: status,
          flow: error.flow || flow,
          type: sendMode,
        })
      } else if (message === 'ACCOUNT_TYPING_REQUIRED') {
        lurker.viewMessageEvt('error', {
          accounts_reason: message?.toLowerCase(),
          accounts_status_code: status,
          login_type: '2fa_phone',
          flow: error.flow || flow,
          type: sendMode,
        })

        storage.set(MANDATORY_TIPIFICATION_KEY, true)

        if (onAccountCompleteData) {
          onAccountCompleteData(error?.response?.data?.token)
        }
      } else {
        lurker.viewMessageEvt(eventName, {
          accounts_reason: 'unauthorized',
          accounts_status_code: status,
          flow: error.flow || flow,
          type: sendMode,
        })
      }

      showError(genericError)
    } else if (status === 429) {
      if (message === 'MAX_ATTEMPTS_CHECK_PIN_CODE') {
        lurker.viewMessageEvt(eventName, {
          accounts_reason: 'max_account_check_attempts',
          accounts_status_code: status,
          flow: error.flow || flow,
          type: sendMode,
        })
        onMaxAccountCheckAttemptReached(details.ttl)
        showError('showMaxAccountCheckAttempts')
      } else {
        lurker.viewMessageEvt(eventName, {
          reason: 'rate_limit_exceeded',
          accounts_status_code: status,
          flow,
        })
        showError('showRateLimitError')
      }
    } else if (status === 403) {
      switch (message) {
        case 'INVALID_CODE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'invalid_pin_code',
            accounts_status_code: status,
            flow,
            type: sendMode,
          })
          showError('showInvalidPinCodeError')
          break
        case 'NO_PIN_CODE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'no_pin_code',
            accounts_status_code: status,
            flow,
            type: sendMode,
          })
          showError(genericError)
          break
        case 'ACCOUNT_LOCKED':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: message.toLowerCase(),
            accounts_status_code: status,
            flow,
            type: sendMode,
          })
          showError('showRateLimitError')
        default:
          lurker.viewMessageEvt(eventName, {
            accounts_reason:
              message && typeof message === 'string'
                ? message.toLowerCase()
                : 'unexpected_status_code',
            accounts_status_code: status,
            flow,
            type: sendMode,
          })
          showError(genericError)
      }
    } else if (status) {
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'unexpected_status_code',
        accounts_status_code: status,
        accounts_error_message: (error && error.message) || 'unknown',
        flow,
        type: sendMode,
      })
      showError(genericError)
    } else {
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'unexpected_error',
        accounts_error_message: (error && error.message) || 'unknown',
        flow,
        type: sendMode,
      })
      showError(genericError)
    }
  }

  const checkCode = (_, pinCode) => {
    const apiCallLogin = isAccountRevalidationExperimentOn(abTests)
      ? loginWith2FAValidation
      : loginWith2FA

    const sendMode = getSendMode(true)

    return apiCallLogin(pinCode, token, LOGIN_TYPE, sessionTokens, abTests).then(response => {
      const { publicAccountId } = response
      lurker.setPublicAccountId(publicAccountId)
      const extraData = getModalLoginExtraData(abTests, modalMode)
      // TODO - Check if we can safelly remove the email_login_success event
      // keeping it for now to avoid losing data while we validate
      lurker.viewMessageEvt('email_login_success', {
        accountId: response.accountsId,
        ...extraData,
        type: sendMode,
        flow: 'phone_2fa',
      })
      lurker.viewMessageEvt('login_success', {
        accountId: response.accountsId,
        flow: 'phone_2fa',
        context: LOGIN_TYPE,
        type: sendMode,
        ...extraData,
      })
      return response
    })
  }

  const onSendCodeClick = type => {
    return sendCode2FA(type, issuer, token)
  }

  const checkAttempt = () => getAttemptCheck2FA(token)

  useEffect(() => {
    checkAttemptLockTime(checkAttempt)
  }, [])

  return (
    <Wrapper>
      <ArrowContainer modalMode={modalMode} onClick={onBackClick}>
        <StyledArrow />
      </ArrowContainer>
      {!modalMode && <StyledTitle className="mb-4 block">Código de verificação</StyledTitle>}

      {modalMode && <span className="typo-title-medium mb-2 block">Código de verificação</span>}

      <ContainerWrapper
        type="outlined"
        paddingBottom="--spacing-8"
        paddingTop="--spacing-8"
        paddingLeft="--spacing-8"
        paddingRight="--spacing-8"
        modalMode={modalMode}
      >
        <CheckPinCodeTemplate
          checkCode={checkCode}
          sendPinCode={onSendCodeClick}
          sendSMS={onSendCodeClick}
          makeCall={onSendCodeClick}
          onResend={() => checkAttemptLockTime(checkAttempt)}
          onSuccess={onSuccess}
          onPinCodeError={onPinCodeError}
          phone={phone}
          lockTime={lockTime}
          publicAccountId={publicAccountId}
          lurker={lurker}
          modalMode={modalMode}
          defaultSendMode={defaultSendMode}
          abTests={abTests}
        />
      </ContainerWrapper>
    </Wrapper>
  )
}

CheckPhone2FAPage.propTypes = {
  onBackClick: PropTypes.func.isRequired,
  onAccountNotValidated: PropTypes.func,
  onSuccess: PropTypes.func,
  phone: PropTypes.string,
  token: PropTypes.string,
  abTests: PropTypes.string,
  sessionTokens: PropTypes.object,
  publicAccountId: PropTypes.string,
  modalMode: PropTypes.bool,
  onAccountCompleteData: PropTypes.func,
  defaultSendMode: PropTypes.string,
}

export { CheckPhone2FAPage }
